import { Checkbox, DatePicker, Input, InputNumber, Radio, Select } from "antd";
import { CountrySelectSearchable } from "components/UI/Selects/CountrySelectSearchable/CountrySelectSearchable.tsx";
import { CurrencySelectSearchable } from "components/UI/Selects/CurrencySelectSearchable/CurrencySelectSearchable.tsx";
import dayjs from "dayjs";
import { FC, ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormFieldTypes } from "types/enums.ts";

interface RuntimeFieldOption {
    visible_description: string;
    value: string;
}

export interface BasicRuntimeFieldDefinition {
    id?: string | null;
    system_name: string;
    is_hidden: boolean;
    is_required: boolean;
    visible_description: string;
    doc_note?: string | null;
    type?: FormFieldTypes | null;
    placeholder?: string | null;
    default_value?: string | null;
    min?: number | null;
    max?: number | null;
    formFieldRadioValues?: RuntimeFieldOption[];
    formFieldSelectItems?: RuntimeFieldOption[];
}

interface BasicRuntimeFormFieldProps {
    field: BasicRuntimeFieldDefinition;
    name: string;
    label?: string;
    className?: string;
    countrySource?: "central" | "tenant";
    countryValue?: "id" | "iso2" | "name";
}

interface BasicRuntimeFormControlProps extends Omit<BasicRuntimeFormFieldProps, "label" | "className"> {
    checkboxLabel?: string;
}

const optionList = (field: BasicRuntimeFieldDefinition) => {
    const values =
        field.type === FormFieldTypes.Radio || field.type === FormFieldTypes.MultiRadio
            ? field.formFieldRadioValues
            : field.formFieldSelectItems;

    return (values ?? []).map((item) => ({ label: item.visible_description, value: item.value }));
};

// Shared basic renderer for structure-driven forms. Conditional visibility and external scenarios
// intentionally remain outside this component until the visual form runtime is defined.
export const BasicRuntimeFormField: FC<BasicRuntimeFormFieldProps> = ({
    field,
    name,
    label,
    className,
    countrySource = "tenant",
    countryValue = "id",
}) => {
    if (field.is_hidden) {
        return null;
    }

    return (
        <div className={className}>
            {field.type !== FormFieldTypes.Checkbox && (
                <label htmlFor={name} className="mb-2 block text-sm text-black/90">
                    {label ?? field.visible_description}
                    {field.is_required && <span className="ml-1 text-[#b91c1c]">*</span>}
                </label>
            )}
            <BasicRuntimeFormControl
                field={field}
                name={name}
                checkboxLabel={label ?? field.visible_description}
                countrySource={countrySource}
                countryValue={countryValue}
            />
        </div>
    );
};

export const BasicRuntimeFormControl: FC<BasicRuntimeFormControlProps> = ({
    field,
    name,
    checkboxLabel,
    countrySource = "tenant",
    countryValue = "id",
}) => {
    const { control } = useFormContext();

    return (
        <Controller
            control={control}
            name={name}
            render={({ field: formField, fieldState }) => {
                const commonProps = {
                    id: formField.name,
                    placeholder: field.placeholder ?? undefined,
                    status: fieldState.error ? ("error" as const) : undefined,
                };
                let input: ReactNode;

                switch (field.type) {
                    case FormFieldTypes.LongText:
                        input = (
                            <Input.TextArea {...commonProps} {...formField} value={String(formField.value ?? "")} />
                        );
                        break;
                    case FormFieldTypes.Number:
                        input = (
                            <InputNumber
                                {...commonProps}
                                {...formField}
                                value={formField.value as number | null}
                                min={field.min ?? undefined}
                                max={field.max ?? undefined}
                                className="w-full"
                            />
                        );
                        break;
                    case FormFieldTypes.Checkbox:
                        input = (
                            <Checkbox
                                id={formField.name}
                                checked={Boolean(formField.value)}
                                onBlur={formField.onBlur}
                                onChange={(event) => formField.onChange(event.target.checked)}
                                ref={formField.ref}
                            >
                                {checkboxLabel ?? field.visible_description}
                            </Checkbox>
                        );
                        break;
                    case FormFieldTypes.Select:
                    case FormFieldTypes.MultiSelect:
                        input = (
                            <Select
                                {...commonProps}
                                {...formField}
                                value={formField.value}
                                mode={field.type === FormFieldTypes.MultiSelect ? "multiple" : undefined}
                                options={optionList(field)}
                                showSearch
                                optionFilterProp="label"
                                className="w-full"
                                style={{ width: "100%" }}
                            />
                        );
                        break;
                    case FormFieldTypes.Radio:
                        input = <Radio.Group {...formField} value={formField.value} options={optionList(field)} />;
                        break;
                    case FormFieldTypes.MultiRadio:
                        input = (
                            <Checkbox.Group
                                {...formField}
                                value={formField.value as string[]}
                                options={optionList(field)}
                            />
                        );
                        break;
                    case FormFieldTypes.Date:
                        input = (
                            <DatePicker
                                {...commonProps}
                                value={
                                    typeof formField.value === "string" && formField.value
                                        ? dayjs(formField.value)
                                        : null
                                }
                                onBlur={formField.onBlur}
                                onChange={(_, dateString) => formField.onChange(dateString)}
                                ref={formField.ref}
                                className="w-full"
                            />
                        );
                        break;
                    case FormFieldTypes.Country:
                        input = (
                            <CountrySelectSearchable
                                {...formField}
                                value={formField.value as string | number}
                                source={countrySource}
                                useIso2AsValue={countryValue === "iso2"}
                                useNameAsValue={countryValue === "name"}
                                className="w-full"
                                style={{ width: "100%" }}
                            />
                        );
                        break;
                    case FormFieldTypes.Currency:
                        input = (
                            <CurrencySelectSearchable
                                {...formField}
                                value={formField.value as string | number}
                                className="w-full"
                                style={{ width: "100%" }}
                            />
                        );
                        break;
                    default:
                        input = (
                            <Input
                                {...commonProps}
                                {...formField}
                                value={String(formField.value ?? "")}
                                type={
                                    field.type === FormFieldTypes.Email
                                        ? "email"
                                        : field.type === FormFieldTypes.Phone
                                          ? "tel"
                                          : "text"
                                }
                            />
                        );
                }

                return (
                    <div>
                        {input}
                        {field.doc_note && <div className="mt-1 text-xs text-black/45">{field.doc_note}</div>}
                        {fieldState.error?.message && (
                            <div className="mt-1 text-sm text-[#ff4d4f]">{fieldState.error.message}</div>
                        )}
                    </div>
                );
            }}
        />
    );
};
